'use client';

import React, { useEffect } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import LeftArrowNav from '@/assets/images/left-arrow-nav-dashboard.svg';
import LogoTree from '@/assets/images/logo-tree.svg';
import Logo from '@/assets/images/logo.svg';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import Button from '@/components/V2/Button';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { generateApproperiateURL } from '@/helpers/utils/urlHelpers';
import { useAppContext } from '@/layouts/AppProvider';

import { useDashboardProvider } from './DashboardProvider';
import Sidebar from './Sidebar';

const DashboardSidebar = () => {
  const { toggleMenu, setToggleMenu, hideMenu } = useDashboardProvider();
  const { isBackOffice } = useAppContext();
  const { customer } = useAuthContext();
  const { webalias } = useWebAlias();
  const pathname = usePathname();

  let logoRedirectLink = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias ?? ''}`
    : generateApproperiateURL('/', webalias, isBackOffice);

  useEffect(() => {
    const userClickedSidebar = localStorage.getItem('sidebar-nav-clicked') === 'true';
    const isCartPage = pathname?.includes('/cart');

    if (userClickedSidebar) {
      setTimeout(() => {
        setToggleMenu(true);
      }, 100);

      localStorage.removeItem('sidebar-nav-clicked');
    } else if (!isCartPage) {
      setToggleMenu(toggleMenu);
    }
  }, [pathname]);

  return (
    <aside
      className={cn(
        'v2-bg-white v2-border-r v2-border-[#EAECF0] v2-py-6 md:v2-py-6 sm:v2-py-4 xs:v2-py-4 v2-fixed v2-h-full v2-transition-all v2-duration-300 v2-ease-in-out v2-left-0 v2-top-0 v2-w-[316px] v2-z-[3]',
        {
          '!v2-w-[74px]': toggleMenu,
          'v2-hidden': hideMenu,
          '!v2-block v2-z-[2]': !toggleMenu && hideMenu,
        }
      )}
    >
      <div className={cn('v2-relative v2-mb-[24px]', toggleMenu ? 'v2-px-3' : 'v2-px-4')}>
        <Link
          href={logoRedirectLink}
          target="_blank"
          className={cn(
            'v2-max-w-[209px] md:v2-max-w-[209px] sm:v2-max-w-[110px] xs:v2-max-w-[110px] v2-w-full v2-flex v2-items-start',
            {
              'v2-hidden': toggleMenu,
            }
          )}
        >
          <Image src={Logo} alt="Logo" />
        </Link>
        <Link
          href={logoRedirectLink}
          target="_blank"
          className={cn('v2-hidden v2-w-[46px] v2-mx-auto', {
            '!v2-flex': toggleMenu,
          })}
        >
          <Image src={LogoTree} alt="Logo" />
        </Link>

        <Button
          btnSize="sm"
          scheme="text"
          type="button"
          variant="filled"
          onClick={() => setToggleMenu(!toggleMenu)}
          className={cn('v2-cursor-pointer v2-absolute v2-top-2 v2-right-4 !v2-p-0 !v2-bg-transparent', {
            '!v2-right-[-15px] v2-rotate-180': toggleMenu,
            '': !toggleMenu,
          })}
        >
          <Image src={LeftArrowNav} alt="" />
        </Button>
      </div>
      <Sidebar />
    </aside>
  );
};

export default DashboardSidebar;
